import type { Breakpoint, CSSObject } from '@mui/material/styles';
import { createTheme as getTheme } from '@mui/material/styles';

import { remToPx } from 'minimal-shared/utils';

/**
 * The original theme has not been customized.
 * Only use non-styling features such as breakpoints...
 */
const defaultMuiTheme = getTheme();

/**
 * @usage
 * ...theme.mixins.textGradient(`to right, ${theme.vars.palette.text.primary}, ${alpha(theme.vars.palette.text.primary, 0.2)}`
 */
export function textGradient(color?: string): CSSObject {
    return {
        background: `linear-gradient(${color})`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        textFillColor: 'transparent',
        color: 'transparent',
    };
}

/**
 * @usage
 * ...theme.mixins.maxLine({ line: 2, persistent: theme.typography.caption }),
 */
export type MediaFontSize = {
    [key: string]: {
        fontSize: React.CSSProperties['fontSize'];
    };
};

export type MaxLineProps = {
    line: number;
    persistent?: Partial<React.CSSProperties>;
};

/**
 * Converts a CSS font-size value to pixels.
 * If the font size is a string (assumed to be in rem), it converts it to pixels.
 * If the font size is already a number (assumed to be in pixels), it returns it as is.
 *
 * @param fontSize - The font size value to convert, can be a string (e.g., "1.5rem") or a number (e.g., 24)
 * @returns The font size in pixels as a number
 */
function getFontSize(fontSize: React.CSSProperties['fontSize']) {
    return typeof fontSize === 'string' ? remToPx(fontSize) : fontSize;
}

/**
 * Calculates the normalized line height value.
 *
 * If `lineHeight` is a string (presumably in "rem" units), it converts it to pixels
 * and then divides by the font size to get the unitless line height.
 * If `fontSize` is not provided, it defaults to a unitless line height of 1.
 *
 * If `lineHeight` is already a number, it returns it as is.
 *
 * @param lineHeight - The line height value, either as a string (e.g., "1.5rem") or a number
 * @param fontSize - Optional. The font size in pixels to normalize the line height against
 * @returns The normalized line height value as a number
 */
function getLineHeight(lineHeight: React.CSSProperties['lineHeight'], fontSize?: number) {
    if (typeof lineHeight === 'string') {
        return fontSize ? remToPx(lineHeight) / fontSize : 1;
    }

    return lineHeight;
}

/**
 * Calculates the total height of text based on font size, line height, and number of lines.
 *
 * @param fontSize - The size of the font in pixels.
 * @param lineHeight - The line height as a multiplier of font size.
 * @param line - The number of lines of text.
 * @returns The calculated height in pixels.
 */
function calculateHeight(fontSize: number, lineHeight: number, line: number): number {
    return fontSize * lineHeight * line;
}

/**
 * Creates CSS styles for limiting text to a specified number of lines with optional ellipsis.
 * Can maintain consistent height across different breakpoints when `persistent` option is provided.
 *
 * @param {Object} options - Configuration options for the max line styles
 * @param {number} options.line - Maximum number of lines to display
 * @param {Object} [options.persistent] - Settings for maintaining consistent height across breakpoints
 * @param {string|number} options.persistent.fontSize - Base font size
 * @param {string|number} options.persistent.lineHeight - Base line height
 * @param {MediaFontSize} [options.persistent] - Font size configurations for different breakpoints
 * @returns {CSSObject} CSS styles object with text truncation properties
 *
 * @example
 * // Basic usage with 2 lines maximum
 * const styles = maxLine({ line: 2 });
 *
 * @example
 * // With persistent height across breakpoints
 * const styles = maxLine({
 *   line: 3,
 *   persistent: {
 *     fontSize: '16px',
 *     lineHeight: 1.5,
 *     [theme.breakpoints.up('md')]: { fontSize: '18px' }
 *   }
 * });
 */
export function maxLine({ line, persistent }: MaxLineProps): CSSObject {
    const breakpoints: Breakpoint[] = defaultMuiTheme.breakpoints.keys;

    const baseStyles: CSSObject = {
        overflow: 'hidden',
        display: '-webkit-box',
        textOverflow: 'ellipsis',
        WebkitLineClamp: line,
        WebkitBoxOrient: 'vertical',
    };

    if (!persistent) {
        return baseStyles;
    }

    const fontSizeBase = getFontSize(persistent.fontSize);
    const lineHeight = getLineHeight(persistent.lineHeight, fontSizeBase);

    if (!lineHeight || !fontSizeBase) {
        return baseStyles;
    }

    const responsiveStyles = breakpoints.reduce((acc, breakpoint) => {
        const fontSize = getFontSize(
            (persistent as MediaFontSize)[defaultMuiTheme.breakpoints.up(breakpoint)]?.fontSize,
        );

        if (fontSize) {
            acc[defaultMuiTheme.breakpoints.up(breakpoint)] = {
                height: calculateHeight(fontSize, lineHeight, line),
            };
        }

        return acc;
    }, {} as CSSObject);

    return {
        ...baseStyles,
        height: calculateHeight(fontSizeBase, lineHeight, line),
        ...responsiveStyles,
    };
}
